import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import * as logger from '@happyreturns/jscommons';
import LoadingSpinner from 'hrreact/components/loadingSpinner';
import NavBar from 'hrreact/components/navBar';
import Container from './components/Container';
import TrackingBox from './boxes/trackingBox';
import ContactBox from './boxes/contactBox';
import MarketingBox from './boxes/marketingBox';
import ErrorBox from './boxes/errorBox';
import { humanReadableName, loggedFetch } from './utility';
import { logVisit } from './analytics';

function BrandedTrackingPage(props) {
  //------------------------------------------------------------------------------------------------
  // STATE
  //------------------------------------------------------------------------------------------------

  // strings representing carrier and tracking of package being displayed
  const [carrier, setCarrier] = useState(undefined);
  const [tracking, setTracking] = useState(undefined);

  // the site currently operates in two modes:
  //  - regular mode: a package is being sent from a retailer (or similar) to a customer
  //  - return mode: the customer is shipping something back to a retailer (or similar)
  // this boolean flag is true when we're in return mode
  const [returnMode, setReturnMode] = useState(undefined);

  //----------
  // AT MOST ONE OF THE FOLLOWING IS TRUTHY, AND DICATES THE RENDERING OF THIS COMPONENT
  //
  // let's start out in the loading mode... once everything is loaded, this flag should be lowered
  const [isLoading, setIsLoading] = useState(true);
  //
  // error flags
  const [isSystemError, setIsSystemError] = useState(false);
  const [isNotFoundError, setIsNotFoundError] = useState(false);
  //
  // tracking content to display
  const [trackingInfo, setTrackingInfo] = useState(undefined);
  //----------

  //------------------------------------------------------------------------------------------------
  // LIFECYCLE METHODS
  //------------------------------------------------------------------------------------------------
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    let carrierParam = searchParams.get('carrier');
    if (carrierParam) {
      carrierParam = carrierParam.toLowerCase();
    }
    let trackingParam = searchParams.get('tracking');
    let returnMode = searchParams.get('return');

    setCarrier(carrierParam);
    setTracking(trackingParam);

    // force return mode to false when no URL param is provided
    setReturnMode(!!returnMode);

    if (trackingParam) {
      // for now, let's consider this our user ID
      logger.SetUser(carrierParam + '.' + trackingParam);

      fetchTrackingInfo(carrierParam, trackingParam);
    }
    // TODO: show failure page when parameters invalid
  }, []);

  //------------------------------------------------------------------------------------------------
  // CORE FUNCTIONALITY
  //------------------------------------------------------------------------------------------------
  async function fetchTrackingInfo(carrier, tracking) {
    // this flag will be raised if we successfully fetch the tracking info
    let success = false;
    let url = `/tracking/?tracking=${tracking}`;
    // If carrier is null don't include it in the url.
    // JS will convert it to string 'null' instead of an empty value.
    if (carrier) {
      url += `&carrier=${carrier}`;
    }

    try {
      let response = await loggedFetch(url);
      if (!response.ok) {
        const text = await response.text();

        // for some reason we get 500s with the text "This tracking number cannot be found."
        // let's log that as a not found
        //
        // XXX: fix response code on shipping server, not-found should be 4xx not 5xx
        if (
          response.status === 404 ||
          text.includes('package not found') ||
          text.includes('This tracking number cannot be found.')
        ) {
          setIsNotFoundError(true);
        } else {
          setIsSystemError(true);
        }
      } else {
        const trackingInfoResponse = await response.json();

        setTrackingInfo(trackingInfoResponse.trackingInfo);
        setCarrier(trackingInfoResponse.carrier);
        logger.SetUser(
          trackingInfoResponse.carrier +
            '.' +
            trackingInfoResponse.trackingInfo,
        );
        carrier = trackingInfoResponse.carrier;
        success = true;
      }
    } catch (err) {
      logger.Error('Error fetching return methods from the server', err);
    } finally {
      setIsLoading(false);
    }

    // if we failed to set the trackingInfo here, that means we won't log the visit
    // by loading the TrackingBox component
    //
    // we still need to log the visit, so let's log the visit here
    //
    // XXX: in the ideal architecture our shipping service would return a status and we could log
    //      the visit here for all statuses. i hope to be able to further solidigy the shipping service
    //      like this in the future but there are some questions as to how this should be done. (whose
    //      responsibility is it to interpret the information coming back from the carrier, the shipping service
    //      or tracking?). Hardcoding this logic into shipipng may make it not useful for other products
    //      that see the world differently.
    if (!success) {
      logVisit(carrier, tracking, 'unknown');
    }
  }

  //------------------------------------------------------------------------------------------------
  // RENDERING
  //------------------------------------------------------------------------------------------------
  function renderNavBar() {
    return <NavBar homeURL={STYLES.home_url} />;
  }

  // a dedicated function to render the page internals because we have slightly complex logic
  function renderPageContents() {
    // Show loader if it's needed
    if (isLoading) {
      return (
        <div className="py-lg" data-testid="loading-spinner">
          <LoadingSpinner />
        </div>
      );
    }

    // after loading, check for an error mode being asked for by an incoming prop
    if (
      props.errorMode || // errorMode prop is set
      !carrier ||
      !tracking || // carrier or tracking# is missing
      !humanReadableName(carrier) // carrier is not one of our legal carriers
    ) {
      return <ErrorBox />;
    }

    // If we have a trackingInfo, show the main page layout
    // In case of system error, we also show this layout -- passing a null trackingInfo into
    // the TrackingBox will render it in error mode
    if (trackingInfo || isSystemError) {
      return (
        <div className="branded-tracking-content">
          {/* The first column contains the tracking information and (if present) the contact link */}
          <div className="branded-tracking-content-column1">
            <TrackingBox
              trackingInfo={trackingInfo}
              carrier={carrier}
              tracking={tracking}
              returnMode={returnMode}
            />

            {(STYLES || {}).contact_url && (
              <ContactBox contactURL={STYLES.contact_url} />
            )}
          </div>

          {/* The second column contains the marketing image and link */}
          {/* marketingURL is optional to render the marketing box */}
          {(STYLES || {}).marketing_image_url && (
            <MarketingBox
              marketingURL={STYLES.marketing_url}
              marketingImageURL={STYLES.marketing_image_url}
            />
          )}
        </div>
      );
    }

    if (isNotFoundError) {
      return <ErrorBox returnMode={returnMode} />;
    }
  }

  const navbar = renderNavBar();

  return (
    STYLES && (
      <Container
        header={navbar}
        boxStyle={'container-inner'}
        hidePoweredByHR={STYLES.is_white_label}
      >
        <div className="branded-tracking-page" style={{ minHeight: '100%' }}>
          {renderPageContents()}
        </div>
      </Container>
    )
  );
}
BrandedTrackingPage.propTypes = {
  // if this is set, instead of showing package status this page will show the
  // provided error message
  errorMode: PropTypes.string,
};
export default withRouter(BrandedTrackingPage);
